import { DateTime } from '@ksyos/date-time';
import { Alert, Box, Button, Card, FormGroup, FormParagraph, Link, Modal, Text, Tooltip } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { ManagerAuthorization, ToastType, TokenType, TwoFactorAuthenticationType } from '@common/enums';
import { getDisplayName } from '@common/utils/getDisplayName';
import {
    noMfaTimeAmount,
    noMfaTimeUnit,
    practitionerHasMfaMethod,
    practitionerHasMfaTemporarilyDisabled,
} from '@common/utils/isMfaVerificationDisabled';
import { AbortableFetch } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import { hasOneAuthorization, showToast } from '@frontend/utils';
import type { IPractitioner } from '../../interfaces/practitioner';
import { ConditionalWrapper } from '../../elements/HoC/ConditionalWrapper';
import { SetMfaModal } from './SetMfaModal';

export interface ITwoFactorAuthenticationSettingsProps {
    isOwnProfile: boolean;
    onAppEnableChange: (value: boolean) => void;
    practitionerId: string;
}

export function TwoFactorAuthenticationSettings({
    practitionerId,
    onAppEnableChange,
    isOwnProfile,
}: ITwoFactorAuthenticationSettingsProps) {
    const [twoFactorAuthenticationType, setTwoFactorAuthenticationType] =
        useState<TwoFactorAuthenticationType | null>();
    const abortableFetch = new AbortableFetch();
    const [practitioner, setPractitioner] = useState<IPractitioner>();
    const [showNoEmailModal, setShowNoEmailModal] = useState<boolean>(false);
    const [showSendEmailModal, setShowSendEmailModal] = useState<boolean>(false);
    const [showSendHardwareTokenEmailModal, setShowSendHardwareTokenEmailModal] = useState<boolean>(false);
    const [showHardwareTokenNoEmailModal, setShowHardwareTokenNoEmailModal] = useState<boolean>(false);
    const [showSetMfaModal, setShowSetMfaModal] = useState<boolean>(false);
    const [resetMfaTokenCreatedAt, setResetMfaTokenCreatedAt] = useState<Date>();
    const [mfaVerificationSwitchedOffOn, setMfaVerificationSwitchedOffOn] = useState<Date>();
    const [hardwareTokenCreatedAt, setHardwareTokenCreatedAt] = useState<Date>();
    const [serialNumber, setSerialNumber] = useState<string | null>(null);
    const [showDisableMfaModal, setShowDisableMfaModal] = useState<boolean>(false);
    const [actualMfaMethod, setActualMfaMethod] = useState<TwoFactorAuthenticationType | null>();

    const practitionerHasMfaDisabled = !!practitionerHasMfaTemporarilyDisabled(mfaVerificationSwitchedOffOn);

    const canManageMfa = hasOneAuthorization([
        ManagerAuthorization.practitionerAddEdit,
        ManagerAuthorization.adminsAddEdit,
    ]);

    useEffect(() => {
        loadTwoFactorAuthenticationSettings();
        loadPractitioner();
        if (canManageMfa && !isOwnProfile) {
            loadMfaTokenCreationTime();
            loadHardwareTokenCreationTime();
        }
    }, [practitionerId]);

    useEffect(() => {
        return () => abortableFetch.abort();
    }, []);

    const twoFactorAuthenticationText = isOwnProfile
        ? 'Als je direct inlogt op het Ksyos platform is tweestapsverificatie altijd verplicht. Heb je een nieuwe smartphone of verificatieapp? Stel dan tweestapverficatie opnieuw in. Je kunt hier ook wisselen van methode (verificatieapp of hardware token).'
        : 'Help hier de gebruiker met de tweestapsverificatie van zijn of haar account. Zet alleen tweestapsverificatie uit als het echt niet anders kan.';

    return (
        <>
            <Card>
                <Card.Header title={translateKey('profile.twoStepVerification')} />
                <Card.Body>
                    {twoFactorAuthenticationType !== undefined && (
                        <ConditionalWrapper
                            condition={twoFactorAuthenticationType !== null && isOwnProfile}
                            wrapper={(children) => (
                                <Tooltip content="Wil je tweestapsverificatie weer uitzetten? Bel daarvoor de Ksyos servicedesk op +31 (0)20 - 600 00 60.">
                                    {children}
                                </Tooltip>
                            )}
                        ></ConditionalWrapper>
                    )}
                    <Text
                        color="subtle"
                        mb={4}
                    >
                        {twoFactorAuthenticationText}
                    </Text>
                    {practitionerHasMfaDisabled && (
                        <FormGroup label={'Tweestapsverificatie niet verplicht tot'}>
                            <FormParagraph>
                                {DateTime.from(mfaVerificationSwitchedOffOn ?? new Date())
                                    .add(noMfaTimeAmount, noMfaTimeUnit)
                                    .format('DD-MM-YYYY')}
                            </FormParagraph>
                        </FormGroup>
                    )}
                    <FormGroup label={'Huidige methode tweestapsverificatie'}>
                        <FormParagraph>{getDisplayableMfaMethod()}</FormParagraph>
                    </FormGroup>

                    {practitionerHasMfaDisabled && isOwnProfile && (
                        <Alert
                            appearance="warning"
                            hasIcon={true}
                        >
                            Tweestapsverificatie is tijdelijk niet verplicht voor jouw account. Stel het vóór{' '}
                            {DateTime.from(mfaVerificationSwitchedOffOn ?? new Date())
                                .add(noMfaTimeAmount, noMfaTimeUnit)
                                .format('DD-MM-YYYY')}{' '}
                            opnieuw in, om te voorkomen dat je dan niet kunt inloggen.
                        </Alert>
                    )}
                    {isOwnProfile && (
                        <Button
                            onClick={() => setShowSetMfaModal(true)}
                            style={{ whiteSpace: 'normal', textAlign: 'left' }}
                            variant="link"
                        >
                            {twoFactorAuthenticationType === null
                                ? 'Stel tweestapsverificatie in'
                                : 'Stel tweestapsverificatie opnieuw in of wissel van methode'}
                        </Button>
                    )}

                    {canManageMfa && !isOwnProfile && (
                        <Box
                            alignItems="start"
                            display="flex"
                            flexDirection="column"
                            gap={2}
                        >
                            <Box>
                                <Button
                                    isDisabled={!practitioner?.isActivated}
                                    onClick={() => {
                                        practitioner?.emailAddress
                                            ? setShowSendEmailModal(true)
                                            : setShowNoEmailModal(true);
                                    }}
                                    variant="link"
                                >
                                    Verzend e-mail voor instellen verificatieapp
                                </Button>
                                {!!resetMfaTokenCreatedAt && (
                                    <Text
                                        color="minimal"
                                        size="sm"
                                    >{` (Verzonden op ${DateTime.from(resetMfaTokenCreatedAt).format(
                                        'DD-MM-YYYY [om] HH:mm'
                                    )})`}</Text>
                                )}
                            </Box>
                            <Box>
                                <Button
                                    isDisabled={!practitioner?.isActivated}
                                    onClick={() => {
                                        practitioner?.emailAddress
                                            ? setShowSendHardwareTokenEmailModal(true)
                                            : setShowHardwareTokenNoEmailModal(true);
                                    }}
                                    variant="link"
                                >
                                    Verzend e-mail voor registreren hardware token
                                </Button>
                                {!!hardwareTokenCreatedAt && (
                                    <Text
                                        color="minimal"
                                        size="sm"
                                    >{` (Verzonden op ${DateTime.from(hardwareTokenCreatedAt).format(
                                        'DD-MM-YYYY [om] HH:mm'
                                    )})`}</Text>
                                )}
                            </Box>
                            <Button
                                isDisabled={!practitioner?.isActivated}
                                onClick={() => setShowDisableMfaModal(true)}
                                variant="link"
                            >
                                {translateKey('profile.disableMfaTemporarily')}
                            </Button>
                        </Box>
                    )}
                </Card.Body>
                <Card.Footer>
                    <Text
                        color="subtle"
                        mb={4}
                        size="sm"
                    >
                        Kan je geen gebruik maken van een verificatieapp op je smartphone? We bieden als alternatieve
                        methode een hardware token aan. Bestel deze via het{' '}
                        <Link
                            color="minimal"
                            href="https://www.ksyos.nl/hardware-token-voor-2fa-bestellen/"
                            openInNewTab
                            variant="externalLink"
                        >
                            bestelformulier
                        </Link>
                        .
                    </Text>
                    <Text
                        color="subtle"
                        size="sm"
                    >
                        Hulp nodig? Bekijk de instructievideo’s over de{' '}
                        <Link
                            color="minimal"
                            href="https://vimeo.com/636932851/339789689c"
                            openInNewTab
                            variant="externalLink"
                        >
                            verificatieapp
                        </Link>{' '}
                        of de{' '}
                        <Link
                            color="minimal"
                            href="https://vimeo.com/636934151/483a00049b"
                            openInNewTab
                            variant="externalLink"
                        >
                            hardware token
                        </Link>{' '}
                        of lees de{' '}
                        <Link
                            color="minimal"
                            href="https://www.ksyos.nl/hulp-bij-tweestapsverificatie/"
                            openInNewTab
                            variant="externalLink"
                        >
                            veelgestelde vragen
                        </Link>
                        .
                    </Text>
                </Card.Footer>
            </Card>

            {showSetMfaModal && (
                <SetMfaModal
                    handleChangeOtpMethod={(method: TwoFactorAuthenticationType) => setActualMfaMethod(method)}
                    handleClose={() => {
                        loadTwoFactorAuthenticationSettings();
                        setShowSetMfaModal(false);
                    }}
                    isOpen={showSetMfaModal}
                />
            )}

            <Modal
                isOpen={showNoEmailModal}
                onClose={() => setShowNoEmailModal(false)}
                size="medium"
            >
                <Modal.Header>Verzend e-mail voor instellen verificatieapp</Modal.Header>
                <Modal.CloseButton></Modal.CloseButton>
                <Modal.Body>
                    <Alert appearance="warning">
                        We hebben geen e-mailadres van deze gebruiker. Vul eerst een e-mailadres in, op de profielpagina
                        van deze gebruiker. Let op: Vul alleen het e-mailadres in als je de identiteit van de gebruiker
                        hebt geverifieerd.
                    </Alert>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={() => setShowNoEmailModal(false)}>{translateKey('common.closeShort')}</Button>
                </Modal.Footer>
            </Modal>

            <Modal
                isOpen={showSendEmailModal}
                onClose={() => setShowSendEmailModal(false)}
                size="medium"
            >
                <Modal.Header>Verifieer eerst de identiteit van de gebruiker</Modal.Header>
                <Modal.CloseButton></Modal.CloseButton>
                <Modal.Body>
                    <Alert appearance="warning">
                        Let op! Verzend de e-mail alleen als je hebt geverifieerd dat het verzoek ook daadwerkelijk van
                        deze gebruiker afkomstig is.
                    </Alert>
                    <Text>
                        Je gaat een e-mail verzenden met daarin een link waarmee de gebruiker (opnieuw) een
                        verifcatieapp kan instellen. De e-mail wordt verstuurd naar <b>{practitioner?.emailAddress}</b>
                    </Text>
                    {practitionerHasMfaDisabled && (
                        <Text fontWeight="bold">
                            Tweestapsverificatie wordt weer verplicht voor dit account na het verzenden van de e-mail.
                        </Text>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        onClick={() => setShowSendEmailModal(false)}
                        variant="outline"
                    >
                        {translateKey('common.cancel')}
                    </Button>
                    <Button
                        className="automation-send-email"
                        onClick={async () => {
                            await handleSendResetMfaEmail();
                            setShowSendEmailModal(false);
                            await loadMfaTokenCreationTime();
                        }}
                    >
                        Verzend e-mail
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                isOpen={showSendHardwareTokenEmailModal}
                onClose={() => setShowSendHardwareTokenEmailModal(false)}
                size="medium"
            >
                <Modal.Header>Verzend e-mail voor registreren hardware tokens</Modal.Header>
                <Modal.CloseButton></Modal.CloseButton>
                <Modal.Body>
                    <Alert appearance="warning">
                        Let op: Verzend de e-mail alleen als je hebt geverifieerd dat het verzoek ook daadwerkelijk van
                        deze gebruiker afkomstig is.
                    </Alert>
                    <Text>
                        Je gaat een e-mail verzenden met daarin een link waarmee de gebruiker een hardware token kan
                        registreren. De e-mail word verstuurd naar <b>{practitioner?.emailAddress}</b>.
                    </Text>
                    <Text>Vergeet niet de hardware token naar de gebruiker op te sturen.</Text>
                    {practitionerHasMfaDisabled && (
                        <Text fontWeight="bold">
                            Tweestapsverificatie wordt weer verplicht voor dit account na het verzenden van de e-mail.
                        </Text>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        onClick={() => setShowSendHardwareTokenEmailModal(false)}
                        variant="outline"
                    >
                        {translateKey('common.cancel')}
                    </Button>
                    <Button
                        onClick={async () => {
                            await handleSendSetHardwareTokenEmail();
                            setShowSendHardwareTokenEmailModal(false);
                            await loadHardwareTokenCreationTime();
                        }}
                    >
                        Verzend e-mail
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                isOpen={showHardwareTokenNoEmailModal}
                onClose={() => setShowHardwareTokenNoEmailModal(false)}
                size="medium"
            >
                <Modal.Header>Verzend e-mail voor registreren hardware tokens</Modal.Header>
                <Modal.CloseButton></Modal.CloseButton>
                <Modal.Body>
                    <Alert appearance="warning">
                        We hebben geen e-mailadres van deze gebruiker. Vul eerst een e-mailadres in, op de profielpagina
                        van deze gebruiker.
                        <br />
                        <br />
                        Let op: vul alleen het e-mailadres in als je de identiteit van de gebruiker hebt geverifieerd.
                    </Alert>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={() => setShowHardwareTokenNoEmailModal(false)}>
                        {translateKey('common.close')}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                isOpen={showDisableMfaModal}
                onClose={() => setShowDisableMfaModal(false)}
                size="medium"
            >
                <Modal.Header>{translateKey('profile.disableMfaTemporarily')}</Modal.Header>
                <Modal.CloseButton></Modal.CloseButton>
                <Modal.Body>
                    <Alert appearance="warning">
                        Let op: doe dit alleen als je hebt geverifieerd dat het verzoek ook daadwerkelijk van deze
                        gebruiker afkomstig is.
                    </Alert>
                    <Text>
                        Tweestapsverificatie wordt 72 uur niet verplicht bij het direct inloggen in het Ksyos platform.
                    </Text>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        onClick={() => setShowDisableMfaModal(false)}
                        variant="outline"
                    >
                        {translateKey('common.cancel')}
                    </Button>
                    <Button onClick={async () => await handleDisableMfaTemporarily()}>2FA niet verplichten</Button>
                </Modal.Footer>
            </Modal>
        </>
    );

    async function handleSendResetMfaEmail() {
        const url = '/api/resetMfa';
        const result = await abortableFetch.fetchJson(url, 'POST', { practitionerId: practitioner?.id });
        if (!result.errorCode) {
            showToast(ToastType.success, `De e-mail is verzonden naar ${practitioner?.emailAddress}`);
        } else {
            showToast(ToastType.error, 'De e-mail kon niet verstuurd worden. Probeer nog een keer');
        }
    }

    async function handleSendSetHardwareTokenEmail() {
        const url = '/api/sendSetHardwareTokenEmail';
        const result = await abortableFetch.fetchJson(url, 'POST', { practitionerId: practitionerId });
        if (!result.errorCode) {
            showToast(ToastType.success, `De e-mail is verzonden naar ${practitioner?.emailAddress}`);
        } else {
            showToast(ToastType.error, 'De e-mail kon niet verstuurd worden. Probeer nog een keer');
        }
    }

    async function loadTwoFactorAuthenticationSettings() {
        const url = `/api/otpMethods/${practitionerId}/twoFactorAuthenticationSettings`;
        const result = await abortableFetch.fetchJson(url, 'GET');
        const isTemporarilyDisabled =
            result.json.twoFactorAuthenticationType === TwoFactorAuthenticationType.temporarilyDisabledMfa;
        const isEnabled =
            !!result.json.otpMethod &&
            !practitionerHasMfaTemporarilyDisabled(result.json.otpMethod.mfaVerificationSwitchedOffOn) &&
            practitionerHasMfaMethod(result.json.otpMethod);

        if (isTemporarilyDisabled) {
            let method = TwoFactorAuthenticationType.temporarilyDisabledMfa;

            if (result.json.otpMethod?.otpAppId) {
                method = TwoFactorAuthenticationType.verificationApp;
            } else if (result.json.otpMethod?.otpDeviceId) {
                method = TwoFactorAuthenticationType.verificationDevice;
            }

            setActualMfaMethod(method);
        }

        setMfaVerificationSwitchedOffOn(result.json.otpMethod?.mfaVerificationSwitchedOffOn);
        setTwoFactorAuthenticationType(result.json.twoFactorAuthenticationType);
        setSerialNumber(result.json.serialNumber);
        onAppEnableChange(!!isEnabled);
    }

    async function loadPractitioner() {
        const url = `/api/practitioners/${practitionerId}`;
        const result = await abortableFetch.fetchJson(url, 'GET');
        setPractitioner(result.json);
    }

    async function loadMfaTokenCreationTime() {
        const url = `/api/activationTokens/${practitionerId}/getTokenCreationTime?tokenType=${TokenType.resetMfa}`;
        const result = await abortableFetch.fetchJson(url, 'GET');
        setResetMfaTokenCreatedAt(result.json.created_at);
    }

    async function loadHardwareTokenCreationTime() {
        const url = `/api/activationTokens/${practitionerId}/getTokenCreationTime?tokenType=${TokenType.setHardwareToken}`;
        const result = await abortableFetch.fetchJson(url, 'GET');
        setHardwareTokenCreatedAt(result.json.created_at);
    }

    async function handleDisableMfaTemporarily() {
        const url = `/api/otpMethods/${practitionerId}/disableMfaTemporarily`;
        const result = await abortableFetch.fetchJson(url, 'POST');

        if (!result.errorCode) {
            showToast(
                ToastType.success,
                `Tweestapsverificatie is tijdelijk niet verplicht voor ${getDisplayName(
                    practitioner!,
                    false,
                    'nameGivenNameFamily'
                )}.`
            );

            setShowDisableMfaModal(false);
            onAppEnableChange(false);
            setMfaVerificationSwitchedOffOn(new Date());
        } else {
            showToast(ToastType.error, 'Tweestapsverificatie kon niet worden uitgezet. Probeer nog een keer.');
        }
    }

    function getDisplayableMfaMethod() {
        const method = actualMfaMethod ?? twoFactorAuthenticationType;

        if (method && method !== TwoFactorAuthenticationType.temporarilyDisabledMfa) {
            return `${translateKey(`twoFactorAuthenticationType.${method}`)} ${
                serialNumber ? ` (${serialNumber})` : ''
            }`;
        } else {
            return translateKey('twoFactorAuthenticationType.none');
        }
    }
}
